import React from 'react'
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from 'components/Select/Select';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';

import { getTransfers, getTransferInfo, updateTransferInfo } from 'api/transfers';

import NewTransferDialog from './components/NewTransferDialog/NewTransferDialog';
import EditSenderDialog from 'pages/Transfer/components/EditSenderDialog';
import EditReceiverDialog from 'pages/Transfer/components/EditReceiverDialog';
import EditAmountDialog from 'pages/Transfer/components/EditAmountDialog';
import ConfirmTransferDeleteDialog from 'pages/Transfer/components/ConfirmTransferDeleteDialog';

const selectTypes = [
    { value: 'pending', label: 'Актуальные' },
    { value: 'month', label: 'За месяц' },
];

const Transfers: React.FC = () => {
    const queryClient = useQueryClient();
    const [selectType, setSelectType] = React.useState('pending');
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [modalOpen, setModalOpen] = React.useState(false);

    const [transferDialogOpen, setTransferDialogOpen] = React.useState(false);
    const [editSenderDialogOpen, setEditSenderDialogOpen] = React.useState(false);
    const [editReceiverDialogOpen, setEditReceiverDialogOpen] = React.useState(false);
    const [editAmountDialogOpen, setEditAmountDialogOpen] = React.useState(false);
    const [confirmTransferDeleteDialogOpen, setConfirmTransferDeleteDialogOpen] = React.useState(false);

    const { data, isLoading } = useQuery(['transfers', selectType], getTransfers);

    const transferQuery = useQuery(['transfer', selectedId], getTransferInfo, {
        enabled: !!selectedId,
    });

    const { control, watch, setValue, handleSubmit, reset } = useForm({
        defaultValues: {
            is_sent: false,
            is_received: false,
            admin_comment: null,
        }
    });

    const updateTransferMutation = useMutation(updateTransferInfo, {
        onSuccess: () => {
            queryClient.invalidateQueries(['transfer', selectedId]);
            queryClient.invalidateQueries(['transfers']);
            reset();
        }
    });

    const onSubmit = handleSubmit((data) => updateTransferMutation.mutate({ transferId: selectedId, ...data }));

    React.useEffect(() => {
        if (transferQuery.data) {
            setValue('admin_comment', transferQuery.data.admin_comment);
            setValue('is_sent', transferQuery.data.is_sent);
            setValue('is_received', transferQuery.data.is_received);
        }
    }, [transferQuery.data, setValue]);

    const goToDetail = ({ data }: any) => {
        setSelectedId(data.id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const formatDate = ({ value }: { value: string }) => {
        return moment(value).format('DD MMM YYYY HH:mm');
    };

    const getStatusClass = ({ value }: { value: string }) => {
        if (value === 'hold') return 'bg-amber-500';
        if (value === 'sent') return 'bg-lime-500';
        if (value === 'received') return 'bg-cyan-500';

        return '';
    };

    const td = transferQuery.data;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end space-x-4">
                <MuiButton type="button" variant="contained" onClick={() => setTransferDialogOpen(true)}>Создать трансфер</MuiButton>
            </div>
            <div className="flex lg:flex-row flex-col lg:space-x-8 lg:space-y-0 space-y-4 mb-8">
                <div className="w-full">
                    <Select
                        label=""
                        options={['pending', 'month']}
                        value={selectType}
                        onChange={setSelectType}
                        multiple={false}
                    />
                </div>
            </div>

            <div className="w-full relative shadow-md rounded overflow-hidden" style={{ height: 800 }}>
                <AgGridReact
                    rowData={data || []}
                    defaultColDef={{
                        flex: 1,
                        sortable: true,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: { suppressFilterButton: true }
                    }}
                    onRowClicked={goToDetail}
                >
                    <AgGridColumn headerName="Рум" field="room_name" filter />
                    <AgGridColumn headerName="Отправитель" field="sender_name" filter />
                    <AgGridColumn headerName="Получатель" field="receiver_name" filter />
                    <AgGridColumn headerName="Сумма" field="amount" />
                    <AgGridColumn headerName="Дата" field="created_datetime" sort="desc" valueFormatter={formatDate} />
                    <AgGridColumn headerName="Статус" field="status" cellClass={getStatusClass} />
                </AgGridReact>
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.8)' }}>
                        <CircularProgress />
                    </div>
                ) : null}
            </div>

            <NewTransferDialog
                open={transferDialogOpen}
                onClose={() => setTransferDialogOpen(false)}
            />

            <Dialog open={modalOpen} onClose={handleCloseModal} TransitionProps={{ onExited: () => { setSelectedId(null); reset(); } }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Трансфер №{selectedId}
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative', minHeight: 500 }}>
                    {transferQuery.isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ zIndex: 1 }}>
                            <CircularProgress />
                        </div>
                    )}
                    <div style={{ opacity: transferQuery.isLoading ? 0 : 1 }}>
                        <p className="text-lg font-light mb-4">Дата создания: {moment(td?.created_datetime).format('DD MMM YYYY HH:mm')}</p>
                        <p className="text-lg font-light mb-4">Рум: {td?.room_name}</p>
                        <p className="text-lg font-light mb-4">
                            Сумма: {td?.amount}
                            <Button
                                type="button"
                                loading={transferQuery.isLoading}
                                onClick={() => setEditAmountDialogOpen(true)}
                            >Изменить</Button>
                        </p>
                        <p className="text-lg font-light mb-4">Комиссия: {td?.fee}</p>
                        <p className="text-lg font-light mb-4">
                            Отправитель: {td?.sender_name}, {td?.send_account_name}
                            <Button
                                type="button"
                                loading={transferQuery.isLoading}
                                onClick={() => setEditSenderDialogOpen(true)}
                            >Изменить</Button>
                        </p>
                        <p className="text-lg font-light mb-4">
                            Получатель: {td?.receiver_name}, {td?.receive_account_name}
                            <Button
                                type="button"
                                loading={transferQuery.isLoading}
                                onClick={() => setEditReceiverDialogOpen(true)}
                            >Изменить</Button>
                        </p>
                        <form className="flex flex-col space-y-4 pt-2" onSubmit={onSubmit} noValidate>
                            <Controller
                                name="is_sent"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        label="Отправлен"
                                        control={<Checkbox {...field} checked={!!field.value} onChange={({ target }) => field.onChange(target.checked)} />}
                                    />
                                )}
                            />
                            <Controller
                                name="is_received"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        label="Получен"
                                        control={<Checkbox {...field} checked={!!field.value} onChange={({ target }) => field.onChange(target.checked)} />}
                                    />
                                )}
                            />
                            <br /><br />
                            <p className="text-lg font-light mb-4">Комментарий отправителя: {td?.sender_comment}</p>
                            <p className="text-lg font-light mb-4">Комментарий получателя: {td?.receiver_comment}</p>
                            <Controller
                                name="admin_comment"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="Комментарий администратора"
                                        rows={3}
                                        multiline
                                        fullWidth
                                        required
                                        {...field}
                                    />
                                )}
                            />
                            <div className="pt-4 space-x-4 text-right">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    loading={updateTransferMutation.isLoading}
                                >Сохранить</Button>
                                <Button
                                    variant="contained"
                                    type="button"
                                    color="error"
                                    onClick={() => setConfirmTransferDeleteDialogOpen(true)}
                                >Удалить</Button>
                            </div>
                        </form>
                    </div>
                </DialogContent>
            </Dialog>

            <EditSenderDialog
                open={editSenderDialogOpen}
                transferId={selectedId}
                roomId={td?.room_id}
                chipCurrencyId={td?.chip_currency_id}
                amount={td?.amount}
                onClose={() => setEditSenderDialogOpen(false)}
            />

            <EditReceiverDialog
                open={editReceiverDialogOpen}
                transferId={selectedId}
                roomId={td?.room_id}
                chipCurrencyId={td?.chip_currency_id}
                onClose={() => setEditReceiverDialogOpen(false)}
            />

            <EditAmountDialog
                open={editAmountDialogOpen}
                transferId={selectedId}
                amount={td?.amount}
                fee={td?.fee}
                onClose={() => setEditAmountDialogOpen(false)}
            />

            <ConfirmTransferDeleteDialog
                open={confirmTransferDeleteDialogOpen}
                transferId={selectedId || ''}
                onClose={() => setConfirmTransferDeleteDialogOpen(false)}
                onDeleted={() => {
                    queryClient.invalidateQueries(['transfers']);
                    setConfirmTransferDeleteDialogOpen(false);
                    setModalOpen(false);
                }}
            />
        </div>
    );
};

export default Transfers;
